// LinkedIn Scrape Status ***************************************************************************
// pathname: GET /v1/resume/linkedin-scrape/{scrapeId}/status
// ************************************************************************************************
// SWR hook to poll the status of a LinkedIn scrape job. Polling stops automatically once the
// status reaches a terminal value (`ready`, `applied`, `failed`).
// ************************************************************************************************
import { useSwrApi } from '@hooks/useSwrApi/useSwrApi';
import type { LinkedinScrapeStatusResponse } from '@type/linkedinScrape';


interface SwrLinkedinScrapeStatusProps {
  scrapeId: string | null;
  locale?: Locale;
  pollIntervalMs?: number;
}


/**
 * @description SWR: poll the status of a LinkedIn scrape job. Returns null `scrapeId` to disable
 * fetching. Polling halts automatically when the status reaches a terminal value.
 *
 * @param props - hook options.
 * @param props.scrapeId - the id of the LinkedIn scrape job; pass `null` to disable fetching.
 * @param props.locale - the locale to use for the request. Default: 'ro'.
 * @param props.pollIntervalMs - the polling interval while the job is in progress. Default: 3000ms.
 */
export const useSwrLinkedinScrapeStatus = (props: SwrLinkedinScrapeStatusProps) => {
  const {
    scrapeId,
    locale = 'ro',
    pollIntervalMs = 3000,
  } = props;

  const {
    data, error, isLoading, isValidating, mutate,
  } = useSwrApi<LinkedinScrapeStatusResponse>({
    // useSwrApi expects a string url; when we don't want to fetch, set shouldFetch=false instead.
    url: scrapeId ? `/v1/resume/linkedin-scrape/${encodeURIComponent(scrapeId)}/status` : '',
    shouldFetch: Boolean(scrapeId),
    locale,
    apiOptions: {
      revalidateOnFocus: false,
      // useSwrApi disables error retry globally (shouldRetryOnError: false,
      // errorRetryCount: 0). SWR does not resume refreshInterval revalidation
      // while a key is in error state — only the error-retry path restarts it —
      // so a single transient failed request (network blip, dev recompile)
      // would freeze this poll loop permanently. Retry at the poll cadence
      // instead; the stale-scrapeId reset in LinkedinMethod stops the loop on
      // genuine 404s by clearing the scrapeId.
      shouldRetryOnError: true,
      errorRetryCount: 20,
      errorRetryInterval: pollIntervalMs,
      // Function form: stop polling when we reach a terminal status.
      refreshInterval: (latest) => {
        if (!latest) return pollIntervalMs;
        const isTerminal = latest.status === 'ready'
          || latest.status === 'applied'
          || latest.status === 'failed';
        return isTerminal ? 0 : pollIntervalMs;
      },
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  };
};
